import React from "react"
import { FieldValues, RegisterOptions, useFormContext } from "react-hook-form"
import { Group, Loader } from "@mantine/core"

import { Icon, TextareaInput } from "@/components"
import classes from "@/components/TextWithPlaceholders/TextAreaWithPlaceholders.module.css"
import { PlaceholderButtonMenu } from "@/pages/texting/components/PlaceholdersMenuDropdown/PlaceholdersMenuDropdown"
import { Placeholder as DjangoPlaceholder } from "@/types/djangio"
import {
    iconList,
    TextWithPlaceholders,
    useTextWithPlaceholders,
} from "@/components/TextWithPlaceholders/TextWithPlaceholders"
import { Button } from "@quorumus/plum"
import { SamplePreviewModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/AI/SamplePreviewModal"
import { useDisclosure } from "@mantine/hooks"

interface Props {
    name: string
    label: string
    placeholder?: string
    rules: Omit<RegisterOptions<FieldValues, string>, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">
    placeholders: DjangoPlaceholder[]
    removeTargetPlaceholders: boolean
    dataCy?: string
    showPreviewButton?: boolean
    withAsterisk?: boolean
}

export const TextAreaWithPlaceholders = ({
    placeholders,
    name,
    label,
    placeholder,
    rules,
    removeTargetPlaceholders,
    dataCy,
    showPreviewButton = false,
    withAsterisk = false,
}: Props) => {
    const { formattedPlaceholders, handleTextSelection, cursorTextRef } = useTextWithPlaceholders(
        placeholders,
        removeTargetPlaceholders,
    )

    const [isSamplePreviewModalOpen, samplePreviewActions] = useDisclosure(false)
    const formContext = useFormContext()
    const text = formContext.getValues(name)

    return (
        <TextWithPlaceholders name={name} label={label} rules={rules} withAsterisk={withAsterisk}>
            <div className={classes.textContainer}>
                <TextareaInput
                    name={name}
                    data-cy={dataCy}
                    classNames={{ input: classes.noBorderBottom }}
                    rules={rules}
                    triggerOnChange={false}
                    placeholder={placeholder}
                    onSelect={handleTextSelection}
                    showError={false}
                />
                <div className={classes.buttonsContainer}>
                    {formattedPlaceholders?.length > 0 ? (
                        <PlaceholderButtonMenu
                            iconsList={iconList}
                            dataCy={`${dataCy}-insert-placeholder-button`}
                            placeholders={formattedPlaceholders}
                            cursorTextRef={cursorTextRef}
                            inputName={name}
                        />
                    ) : (
                        <Loader size={12} />
                    )}
                    {showPreviewButton && (
                        <Button
                            variant="secondary"
                            className={classes.previewButton}
                            onClick={samplePreviewActions.open}
                        >
                            <Group spacing="0.5rem">
                                <Icon icon="eye" />
                                Preview
                            </Group>
                        </Button>
                    )}
                </div>
            </div>
            <SamplePreviewModal
                opened={isSamplePreviewModalOpen}
                onClose={samplePreviewActions.close}
                text={text}
                placeholders={formattedPlaceholders}
            />
        </TextWithPlaceholders>
    )
}
